import React, { useEffect, useState } from 'react';
import { useAuth, apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Calendar, CheckCircle, XCircle } from 'lucide-react';
import { usePublicContent } from '@/lib/content';

export default function BriefingPage() {
  const { user } = useAuth();
  const { event_information: eventInfo } = usePublicContent();
  const [rsvp, setRsvp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    attending: true,
    guest_count: 1,
    dietary_notes: '',
    comments: '',
    overnight_requested: false,
  });

  useEffect(() => {
    apiGet('/api/rsvp/me').then(r => {
      if (r.data.rsvp) {
        setRsvp(r.data.rsvp);
        setForm({
          attending: r.data.rsvp.attending,
          guest_count: r.data.rsvp.guest_count,
          dietary_notes: r.data.rsvp.dietary_notes || '',
          comments: r.data.rsvp.comments || '',
          overnight_requested: r.data.rsvp.overnight_requested || false,
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await apiPost('/api/rsvp', form);
      setRsvp(res.data.rsvp);
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke sende RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-slate-400">Indlæser...</div>;

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="briefing-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{eventInfo.title}</h1>
      <p className="text-sm text-slate-400 mb-8">{eventInfo.short_description}</p>

      {/* Event Info */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Eventdetaljer</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div><span className="text-slate-500">Event:</span> <span className="text-slate-200">Molin30</span></div>
          <div><span className="text-slate-500">Dato:</span> <span className="text-slate-200">{eventInfo.date_text}</span></div>
          {eventInfo.time_text && <div><span className="text-slate-500">Tid:</span> <span className="text-slate-200">{eventInfo.time_text}</span></div>}
          <div><span className="text-slate-500">Dresscode:</span> <span className="text-slate-200">Business elegant</span></div>
          <div><span className="text-slate-500">Sted:</span> <span className="text-slate-200">{eventInfo.location_text}</span></div>
        </div>
        {eventInfo.practical_information && (
          <p className="text-sm text-slate-400 leading-relaxed mt-4 border-t border-slate-700/50 pt-4">{eventInfo.practical_information}</p>
        )}
      </div>

      {/* RSVP Form */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">RSVP-svar</h3>
          {rsvp && (
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${rsvp.attending ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {rsvp.attending ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {rsvp.attending ? 'Bekræftet' : 'Afvist'}
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="rsvp-form">
          {/* Attending */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Deltager du?</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setForm(f => ({...f, attending: true}))}
                className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${form.attending ? 'bg-blue-600/10 border-blue-500/40 text-blue-400' : 'bg-slate-900/30 border-slate-700 text-slate-400 hover:border-slate-600'}`}
                data-testid="rsvp-attending-yes">
                <CheckCircle className="w-4 h-4 inline mr-2" />Ja, jeg kommer
              </button>
              <button type="button" onClick={() => setForm(f => ({...f, attending: false}))}
                className={`flex-1 py-3 rounded-lg border text-sm font-medium transition-all ${!form.attending ? 'bg-red-600/10 border-red-500/40 text-red-400' : 'bg-slate-900/30 border-slate-700 text-slate-400 hover:border-slate-600'}`}
                data-testid="rsvp-attending-no">
                <XCircle className="w-4 h-4 inline mr-2" />Jeg kan ikke komme
              </button>
            </div>
          </div>

          {form.attending && (
            <>
              {/* Guest count */}
              <div>
                <label className="text-sm text-slate-400 block mb-1.5">Antal gæster inklusive dig selv</label>
                <input type="number" min={1} max={user?.max_guests || 5}
                  value={form.guest_count}
                  onChange={(e) => setForm(f => ({...f, guest_count: parseInt(e.target.value) || 1}))}
                  className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none"
                  data-testid="rsvp-guest-count" />
              </div>

              {/* Dietary */}
              <div>
                <label className="text-sm text-slate-400 block mb-1.5">Kosthensyn</label>
                <input type="text" placeholder="Fx vegetar, glutenfri..."
                  value={form.dietary_notes}
                  onChange={(e) => setForm(f => ({...f, dietary_notes: e.target.value}))}
                  className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none placeholder:text-slate-600"
                  data-testid="rsvp-dietary" />
              </div>

              {/* Overnight */}
              {user?.overnight_allowed && (
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={form.overnight_requested}
                    onChange={(e) => setForm(f => ({...f, overnight_requested: e.target.checked}))}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500"
                    data-testid="rsvp-overnight" />
                  <label className="text-sm text-slate-300">Ønsk overnatning</label>
                </div>
              )}

              {/* Comments */}
              <div>
                <label className="text-sm text-slate-400 block mb-1.5">Kommentarer</label>
                <textarea rows={3} placeholder="Noget vi skal vide..."
                  value={form.comments}
                  onChange={(e) => setForm(f => ({...f, comments: e.target.value}))}
                  className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none resize-none placeholder:text-slate-600"
                  data-testid="rsvp-comments" />
              </div>
            </>
          )}

          <Button type="submit" disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 rounded-md disabled:opacity-50"
            data-testid="rsvp-submit-btn">
            {submitting ? 'Sender...' : rsvp ? 'Opdater RSVP' : 'Send RSVP'}
          </Button>
        </form>
      </div>
    </div>
  );
}
